import React, { useEffect, useState } from "react";
import { Box, Button, TextField, Typography, Autocomplete, IconButton, Icon } from "@mui/material";
import {
  getShipmentPurchaseOrdersByShipmentId,
  updateBatchShipmentPurchaseOrders,
} from "../../api/imports";
import { getPurchaseOrderList } from "../../api/purchaseOrders";
import { useSnack } from "../../services/SnakbarProvider";

interface ShipmentPurchaseOrder {
  id: number;
  purchaseOrderId: number;
  portArrivalDate: string;
  plantArrivalDate: string;
  returnDate: string;
  delayCharges: number;
  shipmentId: string;
  purchaseOrder?: {
    id: number;
    code: string;
    provider?: {
      code: string;
    };
  };
}

interface ManageShipmentPurchaseOrdersProps {
  props: {
    id: string;
    companyId: number;
    onSubmit: any;
  };
}

export const ManageShipmentPurchaseOrders: React.FC<
  ManageShipmentPurchaseOrdersProps
> = ({ props }) => {
  const { id, companyId, onSubmit } = props;
  const [purchaseOrders, setPurchaseOrders] = useState<ShipmentPurchaseOrder[]>([]);
  const [availablePurchaseOrders, setAvailablePurchaseOrders] = useState<any[]>([]);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState<number | null>(null);
  const { openSnack } = useSnack();

  const fetchPurchaseOrders = async () => {
    try {
      const response = await getShipmentPurchaseOrdersByShipmentId(id);
      setPurchaseOrders(response.data || []);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      openSnack("Error al cargar órdenes de compra", "error");
    }
  };

  const fetchAvailablePurchaseOrders = async () => {
    try {
      const response = await getPurchaseOrderList({
        companyId,
        pageSize: 1000,
        orderBy: "code",
        orderDirection: "desc",
        statusCodes: ["PO_APROBADA"],
        isImport: true,
      });
      setAvailablePurchaseOrders(response.data || []);
    } catch (error) {
      console.error("Error fetching available purchase orders:", error);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
    fetchAvailablePurchaseOrders();
  }, [id, companyId]);

  const handlePurchaseOrderUpdate = (
    purchaseOrderId: number,
    field: keyof ShipmentPurchaseOrder,
    value: string | number
  ) => {
    setPurchaseOrders((prev) =>
      prev.map((po) =>
        po.purchaseOrderId === purchaseOrderId
          ? { ...po, [field]: value }
          : po
      )
    );
  };

  const handleSave = async () => {
    try {
      const updateData = purchaseOrders.map((po) => ({
        id: po.id,
        purchaseOrderId: po.purchaseOrderId,
        shipmentId: parseInt(id),
        companyId: companyId,
      }));
      await updateBatchShipmentPurchaseOrders(updateData);
      openSnack("Órdenes de compra actualizadas correctamente", "success");
      onSubmit();
    } catch (error) {
      openSnack("Error al actualizar órdenes de compra", "error");
    }
  };

  const handleAddPurchaseOrder = () => {
    if (!selectedPurchaseOrderId) return;

    // Check if already added
    if (purchaseOrders.some((po) => po.purchaseOrderId === selectedPurchaseOrderId)) {
      openSnack("Esta orden de compra ya está agregada", "warning");
      return;
    }

    const selectedPO = availablePurchaseOrders.find(
      (po) => po.id === selectedPurchaseOrderId
    );

    const newPurchaseOrder: ShipmentPurchaseOrder = {
      id: 0, // New item, will be created on save
      purchaseOrderId: selectedPurchaseOrderId,
      shipmentId: id,
      portArrivalDate: "",
      plantArrivalDate: "",
      returnDate: "",
      delayCharges: 0,
      purchaseOrder: selectedPO,
    };

    setPurchaseOrders((prev) => [...prev, newPurchaseOrder]);
    setSelectedPurchaseOrderId(null);
  };

  const handleRemovePurchaseOrder = (purchaseOrderId: number) => {
    setPurchaseOrders((prev) =>
      prev.filter((po) => po.purchaseOrderId !== purchaseOrderId)
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
        <Autocomplete
          fullWidth
          options={availablePurchaseOrders}
          getOptionLabel={(option) => option.code || ""}
          value={
            availablePurchaseOrders.find(
              (po) => po.id === selectedPurchaseOrderId
            ) || null
          }
          onChange={(event, value) => {
            setSelectedPurchaseOrderId(value ? value.id : null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Orden de compra"
              sx={{ flexGrow: 1 }}
            />
          )}
        />
        <Button
          variant="contained"
          onClick={handleAddPurchaseOrder}
          disabled={!selectedPurchaseOrderId}
        >
          Agregar Orden de Compra
        </Button>
      </Box>

      {purchaseOrders.map((po) => (
        <Box
          key={po.purchaseOrderId}
          sx={{
            mb: 3,
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {po.purchaseOrder?.code || `PO ${po.purchaseOrderId}`}
            </Typography>
            <IconButton
              color="error"
              size="small"
              onClick={() => handleRemovePurchaseOrder(po.purchaseOrderId)}
              aria-label="Eliminar"
            >
              <Icon>delete</Icon>
            </IconButton>
          </Box>
        </Box>
      ))}

      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        sx={{ mt: 2 }}
      >
        Guardar
      </Button>
    </Box>
  );
};

